/**
 * What's New registry (openspec/cyclecare-whats-new.md). This file stays small:
 * it only assembles the feed from per-release modules under `releases/` and holds
 * the unread logic. Adding a release = add one file in `releases/` + one import +
 * one array entry here. Content never accumulates in this file.
 *
 * Types live in `./types`; the detail view renders each entry's `sections`
 * (premium components) or `body` (markdown) — see ReleaseNoteModal / ReleaseSections.
 */
import type { WhatsNewEntry } from "./types";
import { takeYouThere } from "./releases/2026-06-30-take-you-there";
import { structuredAnswers } from "./releases/2026-06-28-structured-answers";
import { codeQa } from "./releases/2026-06-27-code-qa";
import { perfFeedbackRelease } from "./releases/2026-06-27-release";
import { onboardingFeedback } from "./releases/2026-06-25-onboarding-feedback";

export type { WhatsNewEntry, ReleaseSection, SceneReply } from "./types";

/**
 * Newest first. entries[0].id is the unread marker. Entries with a `flag` are
 * committed here but only shown once that runtime feature is on (WhatsNew filters
 * by /healthz features) — e.g. codeQa stays hidden until FEATURE_CODE_QA is on.
 */
export const WHATS_NEW: WhatsNewEntry[] = [takeYouThere, structuredAnswers, codeQa, perfFeedbackRelease, onboardingFeedback];

/**
 * Whether the bell should show an unread dot: there is at least one entry and
 * the newest one differs from the last-seen id. Pure (no DOM/localStorage) so it
 * is unit-testable; the component supplies `seen` from localStorage.
 */
export function hasUnread(seen: string, entries: WhatsNewEntry[] = WHATS_NEW): boolean {
  return entries.length > 0 && seen !== entries[0]!.id;
}

/**
 * The entries the bell may show, after dropping any gated on a runtime feature
 * flag that is off. An un-flagged entry always shows; a flagged entry shows only
 * when `features[flag] === true`. Single source of truth for flag visibility, so
 * a release committed flag-off (e.g. R1 `structuredAnswer`) cannot leak into the
 * bell — or trip the unread dot — until the prod flag flips. Pure → unit-testable.
 */
export function visibleEntries(
  features?: Record<string, boolean>,
  entries: WhatsNewEntry[] = WHATS_NEW
): WhatsNewEntry[] {
  return entries.filter((e) => e.flag === undefined || features?.[e.flag] === true);
}

/** localStorage key: id → the date a flag-gated release first went live for this client. */
const PUBLISHED_KEY = "cyclecare.whatsnew.published";

/**
 * The date to DISPLAY for an entry — its real release date, not the authoring/doc
 * date. An un-flagged entry publishes on commit, so `entry.date` already is the
 * release date. A flag-gated entry (e.g. R1) only goes live when its prod flag
 * flips — which can be days after the note was written — so we show the day it
 * actually became visible (captured in `published`), never the stale `date`.
 * Pure → unit-testable.
 */
export function displayDate(entry: WhatsNewEntry, published: Record<string, string>): string {
  if (entry.flag === undefined) return entry.date;
  return published[entry.id] ?? entry.date;
}

/** Read recorded publish dates (id → "YYYY-MM-DD"); tolerant of disabled storage. */
export function readPublished(): Record<string, string> {
  try {
    const raw = localStorage.getItem(PUBLISHED_KEY);
    return raw ? (JSON.parse(raw) as Record<string, string>) : {};
  } catch {
    return {};
  }
}

/**
 * Stamp `today` as the publish date for any flag-gated entry that is now visible
 * for the FIRST time (its flag just flipped for this client). Un-flagged entries
 * are never stamped (their `date` is authoritative). Returns the merged map and
 * persists it only when something changed. `today` = "YYYY-MM-DD".
 */
export function recordPublished(
  visible: WhatsNewEntry[],
  today: string,
  current: Record<string, string> = readPublished()
): Record<string, string> {
  let changed = false;
  const next = { ...current };
  for (const e of visible) {
    if (e.flag !== undefined && next[e.id] === undefined) {
      next[e.id] = today;
      changed = true;
    }
  }
  if (changed) {
    try {
      localStorage.setItem(PUBLISHED_KEY, JSON.stringify(next));
    } catch {
      /* storage unavailable — in-memory map still corrects this render */
    }
  }
  return next;
}
